import React, { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useDimensions } from "./useDimensions";
import useIsMobile from "./useIsMobile";

gsap.registerPlugin(ScrollTrigger);

const countryColors = {
  china: { main: "var(--china)", primary: "#ff420a", secondary: "#ffb8a3" },
  taiwan: { main: "var(--taiwan)", primary: "var(--taiwan)" },
  japan: { main: "var(--japan)", primary: "var(--japan)" },
  usa: { main: "var(--usa)", primary: "var(--usa)" }
};

const getCountryCn = (code) => {
  const countryMap = {
    china: "中国",
    taiwan: "台湾",
    japan: "日本",
    usa: "美国"
  };
  return countryMap[code] || "未知";
};

const ScatterAgeYear = ({ chinaData, taiwanData, usaData, japanData }) => {

  const [selectedDataset, setSelectedDataset] = useState("china");
  const data = selectedDataset === "china" 
  ? chinaData 
  : selectedDataset === "japan" 
  ? japanData 
  : selectedDataset === "usa" 
  ? usaData 
  : taiwanData;

 

  const containerRef = useRef(null);
  const dimensions = useDimensions(containerRef);

  const svgRef = useRef();

  const isMobile = useIsMobile();

  const parsedData = useMemo(() => data
    .map(d => ({
      year: new Date(d.date).getFullYear(),
      age: d.age,
      assailant: d.assailant,
      sex: d.sex,
      attacktype: d.attacktype,
      motivation: d.motivation,
      losttrust: d.losttrust
    }))
  .filter(d => d.age !== undefined), [data]);


  useEffect(() => {
    // if (!data || data.length === 0) return;
    if (!dimensions.width || !parsedData.length) return;

    const margin = { top: 35, right: 1, bottom: 0, left: 35 };
    const { width, height } = dimensions;

    
    
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height + margin.bottom + margin.top);

    d3.select(svgRef.current).selectAll('*').remove();

    const xScale = d3.scaleLinear()
      .domain([2000, 2025])
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      // .domain(d3.extent(parsedData, (d) => d.age))
      .domain([10, 70])
      .nice()
      .range([height - margin.bottom, margin.top]);

    svg.append("g")
      .attr("class", "svg-grid")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(
        d3.axisLeft(yScale)
        .tickSize(-width + margin.left + margin.right)
        .tickFormat("")
      )
      .select(".domain")
      .attr("stroke", "none");

    svg.append("line")
      .attr("x1", margin.left)
      .attr("x2", width - margin.right)
      .attr("y1", yScale(50))
      .attr("y2", yScale(50))
      .attr("stroke", "#ff420a")
      .attr("stroke-width", 2)
      .attr("opacity", 0.1);

    const xAxis = d3.axisBottom(xScale)
      .tickFormat(d3.format("d"))
      .tickPadding(10)
      .tickSize(0);

    const yAxis = d3.axisLeft(yScale)
      .tickSize(0)
      .tickPadding(10)
      .tickFormat((d) => d !== 10 ? d : "");

    svg.append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(xAxis);

    svg.append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left},0)`)
      .call(yAxis)
      .select(".domain")
      .attr("opacity", 0);

    svg.selectAll(".y-axis text")
      .style("fill", function() {
        // Compare the actual text content
        return this.textContent.trim() === "50" ? "var(--china)" : "white";
      });

    svg.append("text")
      .attr("class", "axes")
      .attr("y", 13)
      .attr("x", 10)
      .text("袭击者年龄");

    
    const Tooltip = d3.select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("pointer-events", "none")
      .style("opacity", 0);

    const handleMouseOver = (event, d) => {

      Tooltip.style("opacity", 1);
      d3.select(event.currentTarget)
        // .transition()
        // .duration(200)
        .attr("stroke", "#fff");
    };

    const handleMouseMove = (event, d) => {
      const countryColor = countryColors[selectedDataset].main;
      
      Tooltip
        .html(`
          <b><span style="color: ${countryColor}">${getCountryCn(selectedDataset)}</span>
          <span class="tooltip__num">${d.year}</span></b>年 </br>
          嫌疑人：<span style="white-space: nowrap">${d.assailant}（${d.sex}），</span><span class="tooltip__num"  style="white-space: nowrap"><b>${d.age}</span>岁</b>
          </br>无差别类型：${d.attacktype}`)
        .style("left", (event.pageX + 10) + "px")
        .style("top", (event.pageY - 10) + "px");
    };

    const handleMouseOut = (event) => {

      Tooltip.style("opacity", 0);
      d3.select(event.currentTarget)
        .attr("stroke", 0);
    };

    const jitterAmount = 5; // Offset for overlapping circles
    
    const groupedData = d3.groups(parsedData, d => `${xScale(d.year)},${yScale(d.age)}`);
    
    groupedData.forEach(([key, values]) => {
      values.forEach((d, i) => {
        d.jitter = (i - values.length / 2) * jitterAmount;
      });
    });

    const colors = countryColors[selectedDataset];

    svg.selectAll("circle.data-point")
      .data(parsedData)
      .enter()
      .append("circle")
      .attr("class", "data-point")
      .attr("cx", (d) => xScale(d.year) + d.jitter)
      .attr("cy", (d) => yScale(d.age) + d.jitter)
      .attr("r", 5)
      // .attr("fill", (d) => d.age >= 50 ? "#ff420a" : "#ffb8a3")
      .attr("fill", (d) => selectedDataset === "china" ? (d.age >= 50 ? colors.primary : colors.secondary) : colors.primary)
      .attr("fill-opacity", 0.75)
      .on("mouseover", handleMouseOver)
      .on("mousemove", handleMouseMove)
      .on("mouseout", handleMouseOut);
      // .attr("highlighted", d => d.losttrust === "是" ? "highlighted" : "");


    const highlightedPoints = parsedData.filter(d => d.losttrust === "是");
    // const highlightCircles = svg.selectAll("circle.highlight")
    //   // .data(parsedData.filter(d => d.losttrust === "是"))
    //   .data(highlightedPoints)
    //   .enter()
    //   .append("circle")
    //   .attr("class", "highlight")
    //   .attr("cx", d => xScale(d.year) + d.jitter)
    //   .attr("cy", d => yScale(d.age) + d.jitter)
    //   .attr("r", 7)
    //   .attr("fill", "none")
    //   .attr("stroke", "yellow")
    //   .attr("stroke-width", 1.5)
    //   .attr("opacity", 0);

    // gsap.to(highlightCircles.nodes(), {
    //     scrollTrigger: {
    //       trigger: containerRef.current,
    //       start: isMobile ? "top+=650 bottom" : "top+=600 center-=100",
    //       toggleActions: "play none none reverse",
    //       // markers:true,
    //       onToggle: ({ isActive }) => {
    //         if (isActive && !isMobile) {
    //           gsap.delayedCall(0.1, () =>setSelectedDataset("china"));
    //         }
    //       }
    //     },
    //     opacity: 0.9,
    //     duration: 0.2
    //   });


    if (highlightedPoints.length > 0) {
      const calloutText = svg.append("foreignObject")
        .attr("x", xScale(2013) - 60 )
        .attr("y", yScale(70) + 5)
        .attr("class", "callout")
        .attr("width", 130)
        .attr("height", 60)
        .attr("opacity", 1)
        .append("xhtml:div")
        // .text("大多数出于对政府\n的不满的攻击者年\n龄都在40岁以上");
        // .html("大多数出于对<b>政府</b><b>不满</b>的攻击者年龄都在<b>40岁以上</b>");
        .html("在最近的十年，袭击者年龄超过<b>50岁</b>的无差别袭击事件有增长趋势");

      const calloutLine = svg.append("line")
        .attr("x1", xScale(2013) - 3)
        .attr("y1", yScale(65) + 6)
        .attr("x2", xScale(2013) - 3)
        .attr("y2", yScale(60) + 2)
        .attr("stroke", "yellow")
        .attr("stroke-width", 0.5)
        .attr("opacity", 0.8);

      gsap.set([calloutText.node(), calloutLine.node()], { opacity: 0 });

      gsap.to([calloutText.node(), calloutLine.node()], {
        scrollTrigger: {
          trigger: containerRef.current,
          start: isMobile ? "top+=650 bottom" : "top+=600 center-=100",
          toggleActions: "play none none reverse",
        },
        opacity: 1,
        duration: 0.1
      });
    }


  }, [parsedData, dimensions, selectedDataset]);

  return (
    <div className="grid-container-right" id="scatter-age-year">
      <div className="article-body">
      <div className="spacerdiv__tall" />
        <h2>刻板印象还是现实？</h2>
        <p className="subheader">中国无差别袭击案件中“失意”的中年男性</p>
        <div className="spacerdiv" />
        <p>在这里，中国和其他国家的数据，开始呈现出显著的差异。在美国日本和台湾，无差别袭击事件嫌疑人犯罪年龄，都差不多是30岁上下的男性，而在中国，袭击者的平均年龄为39.2岁，中位值是40岁，而在最近的十年，袭击者年龄超过50岁的无差别袭击事件有增长趋势。</p>
        
        { isMobile ? `` : <div>
          <p>根据洛克菲勒研究所的数据，美国的无差别袭击事件嫌疑人平均年龄30.8岁。歪脑通过滚雪球法整理日本和台湾的数据，日本的无差别袭击嫌疑人平均年龄为30.3岁。台湾的则是30.2岁。</p>
          <p>根据《中国监狱信息指南》中整理的数据，歪脑比较了2000年以来中国的其它故意杀人案。我们发现， 故意杀人案的案犯平均年龄是36.7岁，中位值为35岁。通过统计学进一步计算，无差别袭击者的年龄明显大于其它故意杀人案的嫌疑犯年龄。而在日本，则相反。日本法务部做过的一项无差别犯罪的研究支持此结论，报告提出，无差别攻击的嫌疑人比其它凶杀案的凶犯年纪轻。</p>
        </div> }

        { isMobile ? `` : <div className="spacerdiv" />}

      </div>
      {/* style={{ height:"84vh",  position:"sticky", top:"-10px" }} */}
        <div ref={containerRef} className="container-ref desktop-sticky">
          <h4>中国的袭击者都是年龄更大的中年男子</h4>
  
          <div style={{ display:"flex", justifyContent:"center", paddingTop:"15px"}}>
            <div className="country-selection" >
                  { Object.keys(countryColors).map((value, index) => (
                    <React.Fragment key={value}>
    
                    {index > 0 && ( <div className="vertical-divider" /> )}
    
                      <button
                        onClick={() => setSelectedDataset(value)}
                        style={{
                          background: selectedDataset === value ? "rgba(255,255,255,0.2)" : "transparent",
                          fontWeight: selectedDataset === value ? "bold" : "normal",
                          border: selectedDataset === value ? "1px solid white" : "none",
                          borderRadius:"4px",
                        }}
                      >
                        <span className="country-color-indicator"
                          style={{
                            backgroundColor: countryColors[value].main,
                          }}
                        ></span> &nbsp;
                        {value === "china" ? "中国" : value === "taiwan" ? "台湾" : value === "japan" ? "日本" : "美国"}
                        
                      </button>
                    </React.Fragment>
                  ))}
                </div>
          </div>
          <svg ref={svgRef}></svg>
        </div>

        { isMobile ? <div className="article-body">
            <div className="spacerdiv__tall"/>
            <p>根据洛克菲勒研究所的数据，美国的无差别袭击事件嫌疑人平均年龄30.8岁。歪脑通过滚雪球法整理日本和台湾的数据，日本的无差别袭击嫌疑人平均年龄为30.3岁。台湾的则是30.2岁。</p>
            <p>根据《中国监狱信息指南》中整理的数据，歪脑比较了2000年以来中国的其它故意杀人案。我们发现， 故意杀人案的案犯平均年龄是36.7岁，中位值为35岁。通过统计学进一步计算，无差别袭击者的年龄明显大于其它故意杀人案的嫌疑犯年龄。而在日本，则相反。日本法务部做过的一项无差别犯罪的研究支持此结论，报告提出，无差别攻击的嫌疑人比其它凶杀案的凶犯年纪轻。</p>
            </div> :  `` }

    </div>
  );
};

export default ScatterAgeYear;
